import { useCallback, useMemo } from "react";

type CoursePermissionCourse = {
  addedByUserID?: string;
  instructorID?: string;
  companyID?: string;
  visibilitySource?: string;
  status?: string;
};

type CoursePermissionUser = {
  userId?: string;
  _id?: string;
  companyID?: string;
  branchID?: string;
  assignedCompanies?: string[];
  assignedcompanies?: string[];
};

type UseCoursePermissionsArgs = {
  user?: CoursePermissionUser | null;
  isAdmin?: boolean;
  isCompany?: boolean;
  isCompanyAdmin?: boolean;
  isFellowAdmin?: boolean;
  isCreator?: boolean;
  isCompanyEmployee?: boolean;
  isEmployee?: boolean;
  isManager?: boolean;
  isHR?: boolean;
  isSupervisor?: boolean;
  isRestrictedEmployee?: boolean;
};

function useCoursePermissions({
  user,
  isAdmin = false,
  isCompany = false,
  isCompanyAdmin = false,
  isFellowAdmin = false,
  isCreator = false,
  isCompanyEmployee = false,
  isEmployee = false,
  isManager = false,
  isHR = false,
  isSupervisor = false,
  isRestrictedEmployee = false,
}: UseCoursePermissionsArgs) {
  const currentUserId = user?.userId || user?._id || "";
  const currentCompanyId = user?.companyID || "";
  const assignedCompanyIds = useMemo(
    () => [...(user?.assignedCompanies || []), ...(user?.assignedcompanies || [])].filter(Boolean),
    [user?.assignedCompanies, user?.assignedcompanies]
  );

  const isPureCompanyAccount = useMemo(
    () =>
      isCompany &&
      !isManager &&
      !isHR &&
      !isEmployee &&
      !isSupervisor &&
      !isRestrictedEmployee,
    [isCompany, isManager, isHR, isEmployee, isSupervisor, isRestrictedEmployee]
  );

  const isCentralManagerOrHr = useMemo(
    () => (isManager || isHR) && !user?.branchID,
    [isManager, isHR, user?.branchID]
  );

  const canCreateCourse = useMemo(
    () =>
      isAdmin ||
      isPureCompanyAccount ||
      isCentralManagerOrHr ||
      isCreator ||
      isCompanyAdmin ||
      isFellowAdmin,
    [isAdmin, isPureCompanyAccount, isCentralManagerOrHr, isCreator, isCompanyAdmin, isFellowAdmin]
  );

  const companyOwnerIdForCreate = useMemo(() => {
    if (isPureCompanyAccount) {
      return currentUserId || currentCompanyId || "";
    }
    if (isCentralManagerOrHr) {
      return currentCompanyId || "";
    }
    return "";
  }, [isPureCompanyAccount, isCentralManagerOrHr, currentUserId, currentCompanyId]);

  const isEnrollmentBlockedRole = useMemo(
    () => isAdmin || isCompanyAdmin || isFellowAdmin || isCreator,
    [isAdmin, isCompanyAdmin, isFellowAdmin, isCreator]
  );

  const canEnrollByRole = useMemo(
    () =>
      !isEnrollmentBlockedRole &&
      (isCompany || isCompanyEmployee || isEmployee || isManager || isHR || isSupervisor),
    [isEnrollmentBlockedRole, isCompany, isCompanyEmployee, isEmployee, isManager, isHR, isSupervisor]
  );

  const canAssignCourse = useMemo(
    () => isAdmin || isCompany || isCompanyAdmin || isFellowAdmin || ((isManager || isHR) && !isRestrictedEmployee),
    [isAdmin, isCompany, isCompanyAdmin, isFellowAdmin, isManager, isHR, isRestrictedEmployee]
  );

  const canAssignCourseForCourse = useCallback(
    (course?: CoursePermissionCourse | null): boolean => {
      if (!course) return false;
      const st = (course.status || "").trim().toLowerCase();
      if (st === "draft") return false;
      if (isAdmin || isCompany || isCompanyAdmin || isFellowAdmin) return true;
      if ((isManager || isHR) && !isRestrictedEmployee) {
        if (user?.branchID) {
          return course?.visibilitySource === "assigned_to_branch";
        }
        return (
          course?.visibilitySource === "created_by_company" ||
          course?.visibilitySource === "assigned_to_company" ||
          course?.visibilitySource === "assigned_to_branch" ||
          course?.visibilitySource === "purchased"
        );
      }
      return false;
    },
    [isAdmin, isCompany, isCompanyAdmin, isFellowAdmin, isManager, isHR, isRestrictedEmployee]
  );

  const canEditCourse = useCallback(
    (course?: CoursePermissionCourse | null): boolean => {
      if (!course) return false;

      const isCreatedByMe =
        (course.addedByUserID && course.addedByUserID === currentUserId) ||
        (course.instructorID && course.instructorID === currentUserId);
      const isCompanyScopedCourse = Boolean(
        course.companyID &&
          (course.companyID === currentUserId ||
            (currentCompanyId && course.companyID === currentCompanyId))
      );

      return (
        isAdmin ||
        (isCreator && isCreatedByMe) ||
        ((isCompanyAdmin || isFellowAdmin) && (isCreatedByMe || isCompanyScopedCourse)) ||
        ((isPureCompanyAccount || isCentralManagerOrHr) && isCompanyScopedCourse)
      );
    },
    [
      currentUserId,
      currentCompanyId,
      isAdmin,
      isCreator,
      isCompanyAdmin,
      isFellowAdmin,
      isPureCompanyAccount,
      isCentralManagerOrHr,
    ]
  );

  const hasOwnedCourseAccess = useCallback(
    (course?: CoursePermissionCourse | null): boolean => {
      if (!course) return false;

      const isCreatedByMe =
        (course.addedByUserID && course.addedByUserID === currentUserId) ||
        (course.instructorID && course.instructorID === currentUserId);
      const isCompanyOwnedByMyScope = Boolean(
        course.companyID &&
          (course.companyID === currentUserId ||
            (currentCompanyId && course.companyID === currentCompanyId) ||
            assignedCompanyIds.includes(course.companyID))
      );

      return isCreatedByMe || isCompanyOwnedByMyScope;
    },
    [currentUserId, currentCompanyId, assignedCompanyIds]
  );

  const canShowCourseActions = useCallback(
    (course?: CoursePermissionCourse | null): boolean => {
      if (isAdmin || isCreator) return true;
      if (isPureCompanyAccount || isCentralManagerOrHr) {
        return canEditCourse(course);
      }
      if (!course) return false;
      return Boolean(course.instructorID && course.instructorID === currentUserId);
    },
    [isAdmin, isPureCompanyAccount, isCentralManagerOrHr, isCreator, currentUserId, canEditCourse]
  );

  return {
    currentUserId,
    currentCompanyId,
    companyOwnerIdForCreate,
    isPureCompanyAccount,
    isCentralManagerOrHr,
    canCreateCourse,
    canEnrollByRole,
    canAssignCourse,
    canAssignCourseForCourse,
    canEditCourse,
    hasOwnedCourseAccess,
    canShowCourseActions,
  };
}

export default useCoursePermissions;